import '../../css/homepage.css';
import { render } from '../main';
import { store } from '../state';

const logoUrl = new URL('../../images/logo/logo-white-256w.png', import.meta.url).href;
const logoTextUrl = new URL('../../images/logo-22questions/22questions-nobg-300w.png', import.meta.url).href;
const imgCtaBackground = new URL('../../images/card-images/cta-background.jpg', import.meta.url).href;

// --- Comparison data ---

interface CompareRow {
  feature: string;
  q22: string;
  bumble: string;
}

interface CompareCategory {
  name: string;
  rows: CompareRow[];
  footnote: string;
}

const data: CompareCategory[] = [
  {
    name: 'Cost',
    rows: [
      { feature: 'Cost to use the app', q22: 'Free. Always.', bumble: '$60-90/month' },
      { feature: 'When you pay', q22: 'After a great date (optional tip)', bumble: 'Before you can even match' },
      { feature: 'What payment buys', q22: 'Optional AI extras only. Never dating advantages.', bumble: 'Basic features that should be free' },
    ],
    footnote: 'The person who spends $0 has the exact same experience as someone who buys credits.',
  },
  {
    name: 'Everything free',
    rows: [
      { feature: 'Unlimited likes', q22: 'Free', bumble: 'Paid feature' },
      { feature: 'See who liked you', q22: 'Free', bumble: 'Paid feature' },
      { feature: 'All filters and search', q22: 'Free', bumble: 'Paid feature' },
      { feature: 'Sort results (10+ options)', q22: 'Free', bumble: 'Paid feature' },
      { feature: 'Grid browse or swipe', q22: 'Free', bumble: 'No' },
      { feature: 'Incognito mode', q22: 'Free', bumble: 'Paid feature' },
      { feature: 'Travel mode', q22: 'Free', bumble: 'Paid feature' },
      { feature: 'Up to 100 photos', q22: 'Free', bumble: '6' },
      { feature: 'Hundreds of Q&amp;A to share with matches', q22: 'Free', bumble: 'No' },
      { feature: 'AI icebreakers on every match', q22: 'Free', bumble: 'No' },
      { feature: 'AI text chat', q22: 'Free', bumble: 'No' },
      { feature: 'Web, iOS, and Android', q22: 'Free', bumble: 'iOS and Android only' },
    ],
    footnote: 'Every feature. Every filter. Every person. Free.',
  },
  {
    name: 'Search filters',
    rows: [
      { feature: 'Relationship intent', q22: 'Free', bumble: 'Paid feature' },
      { feature: 'Political views', q22: 'Free', bumble: 'Paid feature' },
      { feature: 'Has kids', q22: 'Free', bumble: 'Paid feature' },
      { feature: 'Height', q22: 'Free', bumble: 'Paid feature' },
      { feature: 'Family plans', q22: 'Free', bumble: 'Paid feature' },
      { feature: 'Religion', q22: 'Free', bumble: 'Paid feature' },
      { feature: 'Education level', q22: 'Free', bumble: 'Paid feature' },
      { feature: 'Exercise', q22: 'Free', bumble: 'Paid feature' },
      { feature: 'Smoking', q22: 'Free', bumble: 'Paid feature' },
      { feature: 'Drinking', q22: 'Free', bumble: 'Paid feature' },
      { feature: 'Cannabis', q22: 'Free', bumble: 'Paid feature' },
      { feature: 'Star sign', q22: 'Free', bumble: 'Paid feature' },
      { feature: 'Gender', q22: 'Free', bumble: 'Free' },
      { feature: 'Age', q22: 'Free', bumble: 'Free' },
      { feature: 'Distance', q22: 'Free', bumble: 'Free' },
      { feature: 'Languages', q22: 'Free', bumble: 'Free' },
      { feature: 'Ethnicity', q22: 'Free', bumble: 'Free' },
    ],
    footnote: 'Every filter Bumble locks behind $60-90/month is free here.',
  },
  {
    name: 'No pressure',
    rows: [
      { feature: 'No response time limits', q22: 'Yes', bumble: 'No' },
      { feature: 'Matches never expire', q22: 'Yes', bumble: 'No' },
      { feature: 'No forced "who messages first" rules', q22: 'Yes', bumble: 'No' },
      { feature: 'No countdown timers', q22: 'Yes', bumble: 'No' },
      { feature: 'Connect on your own schedule', q22: 'Yes', bumble: 'No' },
    ],
    footnote: 'Life is busy enough without an app punishing you for not responding in 24 hours.',
  },
  {
    name: 'Safety',
    rows: [
      { feature: 'AI moderation on every message and image', q22: 'Yes', bumble: 'No' },
      { feature: 'You choose whether to receive images', q22: 'Yes', bumble: 'No' },
      { feature: 'No bots. No spam accounts.', q22: 'Yes', bumble: 'No' },
      { feature: 'Never need to share your phone number', q22: 'Yes', bumble: 'No' },
      { feature: 'Full blocking and reporting', q22: 'Yes', bumble: 'Yes' },
    ],
    footnote: 'Safe by design. Not safe by subscription.',
  },
  {
    name: 'Things that don\'t exist here',
    rows: [
      { feature: 'SuperSwipes', q22: 'Nope!', bumble: 'Paid feature' },
      { feature: 'Spotlights', q22: 'Nope!', bumble: 'Paid feature' },
      { feature: 'Profile boosting', q22: 'Nope!', bumble: 'Paid feature' },
      { feature: 'Paid rewinds', q22: 'Nope!', bumble: 'Paid feature' },
      { feature: 'Paid match extensions', q22: 'Nope!', bumble: 'Paid feature' },
      { feature: 'Priority placement', q22: 'Nope!', bumble: 'Paid feature' },
    ],
    footnote: 'We don\'t sell visibility, urgency, or fake engagement. Everyone plays by the same rules.',
  },
  {
    name: 'Profile depth',
    rows: [
      { feature: 'Photos allowed', q22: '100', bumble: '6' },
      { feature: 'Control exactly what people see on your profile', q22: 'Yes', bumble: 'Limited' },
      { feature: '"Prefer not to answer" on every field', q22: 'Yes', bumble: 'No' },
      { feature: 'Hundreds of Q&amp;A to share with matches', q22: 'Yes', bumble: 'No' },
    ],
    footnote: 'Show as much or as little as you want. Your profile, your rules.',
  },
  {
    name: 'Philosophy',
    rows: [
      { feature: 'No rules about who messages first', q22: 'Yes', bumble: 'No' },
      { feature: 'Designed to get you off the app', q22: 'Yes', bumble: 'No' },
      { feature: 'Equal experience for everyone', q22: 'Yes', bumble: 'No' },
      { feature: 'All orientations and relationship styles. One app.', q22: 'Yes', bumble: 'No' },
    ],
    footnote: 'Your relationships, your rules. We don\'t dictate how you connect.',
  },
];

// --- Rendering helpers ---

const VISIBLE_ROWS = 3;

function cellClass(value: string): string {
  const v = value.toLowerCase();
  if (v.startsWith('$0') || v.startsWith('yes') || v.startsWith('free') || v === 'nope!' || v === '100') return 'cmp-yes';
  if (v === 'no' || v === '6' || v.endsWith('only')) return 'cmp-no';
  if (v === 'paid feature' || v === 'limited') return 'cmp-paywall';
  if (v.startsWith('$')) return 'cmp-price-tag';
  return '';
}

function cellHtml(value: string): string {
  const cls = cellClass(value);
  return cls ? `<span class="cmp-cell ${cls}">${value}</span>` : `<span class="cmp-cell">${value}</span>`;
}

function renderSections(): string {
  let html = '';
  for (let i = 0; i < data.length; i++) {
    const cat = data[i];
    const hasOverflow = cat.rows.length > VISIBLE_ROWS;
    const overflowCount = cat.rows.length - VISIBLE_ROWS;

    html += `<div class="cmp-section" data-section="${i}">`;
    html += `<table class="cmp-table">`;
    html += `<thead><tr>
      <th class="cmp-feature-th">${cat.name}</th>
      <th class="cmp-th-22q">22 Questions</th>
      <th class="cmp-th-bumble">Bumble</th>
    </tr></thead>`;
    html += `<tbody>`;

    for (let j = 0; j < cat.rows.length; j++) {
      const r = cat.rows[j];
      const isOverflow = j >= VISIBLE_ROWS;
      html += `<tr${isOverflow ? ' data-overflow class="cmp-overflow-row"' : ''}>
        <td class="cmp-feature-td">${r.feature}</td>
        <td class="cmp-td-22q">${cellHtml(r.q22)}</td>
        <td>${cellHtml(r.bumble)}</td>
      </tr>`;
    }

    html += `</tbody></table>`;

    if (hasOverflow) {
      html += `<button class="cmp-show-more-btn" data-section="${i}">Show more (${overflowCount} items)&hellip;</button>`;
    }

    html += `<p class="cmp-footnote">* ${cat.footnote}</p>`;
    html += `</div>`;
  }
  return html;
}

// --- Page render ---

export function renderCompare(): void {
  const app = document.getElementById('app')!;
  app.className = 'hp-page';
  document.documentElement.classList.add('hp-active');
  document.body.classList.add('hp-active');

  const isLoggedIn = !!store.getState().accessToken;

  render(`
    <!-- Navigation -->
    <nav class="hp-nav">
      <a href="#/welcome" class="hp-nav-logo">
        <img src="${logoTextUrl}" alt="22 Questions" class="hp-nav-logo-img">
      </a>
      <div class="hp-nav-inner">
        ${isLoggedIn
          ? '<a href="#/about-me" class="btn btn-primary btn-sm">My Account</a>'
          : '<a href="#/login" class="btn btn-primary btn-sm">Sign In</a>'
        }
      </div>
    </nav>

    <!-- Scrollable Content -->
    <div class="hp-content">

    <!-- Hero -->
    <section class="hp-section hp-hero">
      <div class="hp-section-inner">
        <div class="hp-section-card">
          <div class="hp-centered">
            <p class="hp-hero-tagline" style="font-weight: var(--weight-bold);">Feature-for-feature comparison</p>
            <h1 class="hp-hero-title">
              22 Questions vs. <span class="highlight">Bumble</span>
            </h1>
            <p class="hp-hero-subtitle">
              Everything they charge $90/month for. Free here.
            </p>
          </div>
        </div>
      </div>
    </section>

    <!-- Comparison Sections -->
    <section class="hp-section">
      <div class="hp-section-inner">
        <div class="hp-section-card cmp-table-card">
          ${renderSections()}
        </div>
      </div>
    </section>

    <!-- CTA -->
    <section class="hp-section hp-final-cta">
      <div class="hp-section-inner">
        <div class="hp-section-card" style="background-image: url('${imgCtaBackground}');">
          <div class="hp-centered">
            <h2 class="hp-headline" style="margin-bottom: 1rem;">
              Stop paying for features<br>that should be free.
            </h2>
            <p class="hp-subhead" style="margin-bottom: 2rem;">
              Every feature. Every filter. Every person.<br>
              No subscriptions. No manipulation.
            </p>
            <a href="#/login" class="btn btn-primary" style="padding: 1rem 3rem; font-size: var(--text-body); border-radius: var(--radius-pill);">
              Get Started. It's Free.
            </a>
          </div>
        </div>
      </div>
    </section>

    <!-- Footer -->
    <footer class="hp-footer">
      <div class="hp-footer-inner">
        <div class="hp-footer-logo">
          <img src="${logoUrl}" alt="22 Questions">
          <img src="${logoTextUrl}" alt="22 Questions" class="hp-footer-logo-img">
        </div>
        <p class="hp-footer-text">
          Independent. No Match Group. No Bumble Inc. No venture mandate to monetize users.
        </p>
        <div class="hp-footer-legal">
          <a href="#/privacy">Privacy Policy</a>
          <span class="hp-footer-divider">|</span>
          <a href="#/terms">Terms of Service</a>
          <span class="hp-footer-divider">|</span>
          <a href="#/contact">Contact</a>
        </div>
      </div>
    </footer>
    </div><!-- /.hp-content -->
  `, false);

  // Show more / show less toggle
  document.querySelectorAll<HTMLButtonElement>('.cmp-show-more-btn').forEach(btn => {
    btn.addEventListener('click', () => {
      const sectionIdx = btn.dataset.section!;
      const section = document.querySelector(`.cmp-section[data-section="${sectionIdx}"]`)!;
      const overflowRows = section.querySelectorAll<HTMLTableRowElement>('tr[data-overflow]');
      const isExpanded = btn.classList.contains('cmp-expanded');

      if (isExpanded) {
        overflowRows.forEach(row => row.classList.add('cmp-overflow-row'));
        btn.classList.remove('cmp-expanded');
        btn.innerHTML = `Show more (${overflowRows.length} items)&hellip;`;
      } else {
        overflowRows.forEach(row => row.classList.remove('cmp-overflow-row'));
        btn.classList.add('cmp-expanded');
        btn.textContent = 'Show less';
      }
    });
  });
}
